import React, { useMemo } from 'react';
import { Form, Row, Col, Checkbox, Radio, Input, Select, Divider, Button } from 'antd';
import type { AlertForwardRule, AlertForwardMeta } from '../utils/types';
import { useEditRule } from '../utils/hooks';
import { useNavigate } from 'react-router-dom';
import {
  DetailDiv,
  FormItem,
  FormInputItem,
  MetricItem,
  RadioGroupColumn,
  CheckboxSelectAll,
  FormItemAlign,
  FormItemCancel,
  CustomDisabledCheckbox,
  ColumnItem,
  RadioSeverity,
} from '../utils/styled';
import { getMetricDisplayName } from '@utils/metrics/metricHelpers';
import { getFeature } from '@src/globalStore/featureStore';
import featureNames from '@src/utils/featureToggle/constants';

type Props = {
  ruleMetaData: AlertForwardMeta;
  rule?: AlertForwardRule;
};

const AlertRuleDetailContent: React.FC<Props> = ({ ruleMetaData, rule }) => {
  const streamMetadataAccount = getFeature(featureNames.STREAM_METADATA_ACCOUNT);
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const editRuleMutation = useEditRule();
  const columns = ruleMetaData.columns;
  const columnOptions = useMemo(
    () => columns.map((c) => ({ label: c.label, value: c.value })),
    [columns],
  );

  const selectedColumns = useMemo(() => {
    const fromRule = (rule?.columns ?? []).map((v) => v.toLowerCase());
    return columns.filter((c) => fromRule.includes(c.value.toLowerCase())).map((c) => c.value);
  }, [columns, rule]);

  const columnsValue = Form.useWatch('columns', form) as string[] | undefined;
  const allValues = useMemo(() => columnOptions.map((o) => o.value), [columnOptions]);
  const current = columnsValue ?? selectedColumns;
  const rows = Math.ceil(columns.length / 2);

  return (
    <>
      <DetailDiv>Please configure the AI alerts to forward to your CDN partners.</DetailDiv>

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          name: rule?.name ?? '',
          subscriber: rule?.subscriber || ruleMetaData.partners?.[0].value,
          metrics: rule?.metrics ?? ruleMetaData.metrics.map((m) => m.value),
          severity: Number(rule?.severity) || ruleMetaData.severity[0]?.value,
          cdnOnly: rule?.cdnOnly ?? true,
          columns: rule?.columns || ruleMetaData.columns.map((o) => o.value),
        }}
        onFinish={(values) => {
          const payload: AlertForwardRule = {
            ...values,
            id: rule?.id,
          };
          editRuleMutation.mutate(payload as AlertForwardRule, {
            onSuccess: () => {
              navigate('/app/settings/alert-forward-list');
            },
          });
        }}
      >
        <Row gutter={20}>
          <Col span={10}>
            <FormInputItem
              label="Rule Name"
              name="name"
              rules={[{ required: true, message: 'Please enter a rule name' }]}
            >
              <Input placeholder="Enter rule name" />
            </FormInputItem>

            <FormInputItem label="CDN partner" name="subscriber">
              <Select options={ruleMetaData.partners} />
            </FormInputItem>

            <FormItem label="Forward AI alerts for these metrics" name="metrics">
              <MetricItem
                options={ruleMetaData.metrics.map((m) => {
                  let displayName = getMetricDisplayName({ metricKey: m.label });

                  // if streamMetadataAccount FF is true, then show Stream instead of Video
                  if (streamMetadataAccount) {
                    displayName = displayName.replace(/Video/, 'Stream');
                  }
                  return {
                    label: displayName,
                    value: m.value,
                  };
                })}
              />
            </FormItem>

            <FormItem
              label="Forward AI alerts for these severities"
              name="severity"
              rules={[{ required: true, message: 'Please enter a severity level' }]}
            >
              <RadioSeverity
                options={ruleMetaData.severity.map((s) => ({
                  label: s.label,
                  value: Number(s.value),
                }))}
              />
            </FormItem>
          </Col>

          <Col span={12}>
            <Form.Item label="Include AI alert data">
              <CustomDisabledCheckbox disabled checked>
                Time Series Graphs
              </CustomDisabledCheckbox>

              <Form.Item
                name="cdnOnly"
                rules={[{ required: true, message: 'Please select an option' }]}
                noStyle
              >
                <RadioGroupColumn>
                  <Radio value={true}>Show the CDN information only</Radio>
                  <Radio value={false}>Show all traffic</Radio>
                </RadioGroupColumn>
              </Form.Item>
            </Form.Item>
            <Divider />

            <CheckboxSelectAll
              indeterminate={current.length > 0 && current.length < allValues.length}
              checked={current.length === allValues.length}
              onChange={(e) => form.setFieldsValue({ columns: e.target.checked ? allValues : [] })}
            >
              Impacted Session Table (Recommended)
            </CheckboxSelectAll>

            <Form.Item name="columns">
              <ColumnItem rows={rows}>
                {columns.map((f) => (
                  <Checkbox key={f.value} value={f.value}>
                    {f.label}
                  </Checkbox>
                ))}
              </ColumnItem>
            </Form.Item>
          </Col>
        </Row>

        <Divider />

        <FormItemAlign>
          <FormItemCancel
            onClick={() => {
              navigate('/app/settings/alert-forward-list');
            }}
          >
            Cancel
          </FormItemCancel>
          <Button type="primary" htmlType="submit" loading={editRuleMutation.isLoading}>
            Save
          </Button>
        </FormItemAlign>
      </Form>
    </>
  );
};

export default AlertRuleDetailContent;
